import type { PackSubmissionDetail, ScanSeverity } from '@planuze/admin-contracts';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  StatusBadge,
  toast,
} from '@planuze/ui';
import { FilterX, PlayIcon, ShieldCheck, ShieldX } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { PackSubmissionsContent } from '../../content/sections/pack-submissions.js';
import { usePackSubmissionsContent } from '../../content/sections/pack-submissions.js';
import { useScannerRuntimeContent } from '../../content/sections/scanner-runtime.js';
import { useScannerRuntimeStatus } from '../../hooks/use-moderation-config.js';
import { useRescanSubmission } from '../../hooks/use-pack-submissions.js';
import { SegmentedControl, type SegmentOption } from '../filters/segmented-control.js';
import {
  CATEGORY_ORDER,
  categorizeFinding,
  categoryLabel,
  type FindingCategory,
} from './scan-finding-category.js';

type ScanResults = NonNullable<PackSubmissionDetail['scanResults']>;
type Finding = ScanResults['findings'][number];
type Tone = 'destructive' | 'warning' | 'info';

/* Filtro de severidade da aba Scan (ADR-0704): `all` + os 3 valores do `scanSeverityDto`. O
   filtro é client-side puro — os findings já vêm no payload do detalhe (ADR-0462 §3). */
type SeverityFilter = 'all' | ScanSeverity;

/* Array vazio estável — evita nova identidade a cada render quando o scan não rodou (mantém os
   `useMemo` referenciais mesmo antes dos early-returns). */
const NO_FINDINGS: readonly Finding[] = [];

/* Contagem por severidade em uma passada — alimenta o `count` das pills do SegmentedControl. */
function countBySeverity(findings: readonly Finding[]): Record<ScanSeverity, number> {
  const counts: Record<ScanSeverity, number> = { error: 0, warning: 0, info: 0 };
  for (const finding of findings) counts[finding.severity] += 1;
  return counts;
}

/* Severidade → tom do badge (ADR-0462). `error` bloqueia (destructive), `warning`/`info`
   informam. Cobre os 3 valores do `scanSeverityDto`. */
function severityTone(severity: ScanSeverity): Tone {
  switch (severity) {
    case 'error':
      return 'destructive';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
  }
}

/* Severidade dominante de um grupo (relance: error > warning > info) → o severity stripe do
   grupo (journey-spec §32.4). Grupo nunca é vazio quando renderizado. */
function dominantSeverity(findings: readonly Finding[]): ScanSeverity {
  if (findings.some((f) => f.severity === 'error')) return 'error';
  if (findings.some((f) => f.severity === 'warning')) return 'warning';
  return 'info';
}

/* Grupo de findings de uma categoria (ADR-0474): header com rótulo + contador + severity
   stripe (tom dominante), reusando a `FindingRow` as-is. */
function FindingGroup({
  category,
  findings,
  copy,
}: {
  readonly category: FindingCategory;
  readonly findings: readonly Finding[];
  readonly copy: PackSubmissionsContent['scanFindings'];
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <StatusBadge variant={severityTone(dominantSeverity(findings))} dot>
          {categoryLabel(category, copy.categoryLabels)}
        </StatusBadge>
        <span className="text-xs text-muted-foreground">
          {copy.findingsCountLabel(findings.length)}
        </span>
      </div>
      <ul className="space-y-3">
        {findings.map((finding, index) => (
          <FindingRow
            key={`${finding.ruleId}-${finding.file}-${finding.line}-${index}`}
            finding={finding}
            copy={copy}
          />
        ))}
      </ul>
    </section>
  );
}

function FindingRow({
  finding,
  copy,
}: {
  readonly finding: Finding;
  readonly copy: PackSubmissionsContent['scanFindings'];
}) {
  return (
    <li className="rounded-md border border-border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge variant={severityTone(finding.severity)} dot>
          {finding.severity}
        </StatusBadge>
        <span className="font-mono text-xs text-muted-foreground">{finding.ruleId}</span>
        {finding.allowedByManifest === true && (
          <StatusBadge variant="neutral">{copy.allowedByManifestBadge}</StatusBadge>
        )}
      </div>
      <p className="mt-2 text-sm text-foreground">{finding.message}</p>
      <p className="mt-1 font-mono text-xs text-muted-foreground">
        {finding.file}:{finding.line}:{finding.column}
      </p>
      {finding.snippet.length > 0 && (
        <pre className="mt-2 overflow-x-auto rounded bg-muted/40 p-2 font-mono text-xs text-foreground">
          {finding.snippet}
        </pre>
      )}
      {finding.suggestion !== undefined && finding.suggestion.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          {copy.suggestionLabel(finding.suggestion)}
        </p>
      )}
    </li>
  );
}

interface Props {
  readonly scanResults: PackSubmissionDetail['scanResults'];
  /* Disparo manual do scan (ADR-0731): id da submissão + se o staff pode acionar (super_admin/support).
     O botão só aparece quando o scan ainda não rodou (empty-state). */
  readonly submissionId: string;
  readonly canManage: boolean;
}

/* Findings do scanner (ADR-0370) revalidados. `null` = scan não rodou (pending/malformado);
   `findings: []` = scan rodou limpo. Sem `any` — o shape vem do contrato. */
export function ScanFindingsCard({ scanResults, submissionId, canManage }: Props) {
  const copy = usePackSubmissionsContent();
  const sf = copy.scanFindings;
  const runtimeCopy = useScannerRuntimeContent();
  const [severity, setSeverity] = useState<SeverityFilter>('all');
  const rescan = useRescanSubmission();
  const runtime = useScannerRuntimeStatus(canManage && scanResults == null);
  const runScan = (): void => {
    rescan.mutate(submissionId, {
      onSuccess: (res) => {
        if (res.status === 'enqueued') toast.success(sf.scanTriggeredToast);
        else if (res.status === 'runner_unavailable') {
          toast.error(sf.runnerUnavailableToast);
        } else toast.error(sf.notScannableToast);
      },
      onError: () => toast.error(sf.triggerErrorToast),
    });
  };
  /* Hooks antes dos early-returns (regras do React): `findings` cai em `NO_FINDINGS` quando o
     scan não rodou, mas o componente retorna antes de usar o resultado. */
  const findings: readonly Finding[] = scanResults?.findings ?? NO_FINDINGS;
  const options = useMemo<readonly SegmentOption<SeverityFilter>[]>(() => {
    const counts = countBySeverity(findings);
    return [
      { value: 'all', label: sf.allSeverityLabel, count: findings.length },
      { value: 'error', label: 'error', count: counts.error },
      { value: 'warning', label: 'warning', count: counts.warning },
      { value: 'info', label: 'info', count: counts.info },
    ];
  }, [findings, sf.allSeverityLabel]);
  const filtered = useMemo(
    () => (severity === 'all' ? findings : findings.filter((f) => f.severity === severity)),
    [findings, severity],
  );

  if (scanResults === null || scanResults === undefined) {
    const runtimeStatus = runtime.data;
    const scanAvailable = runtimeStatus?.manualScan.available === true && !runtime.isFetching;
    let runtimeMessage = runtimeCopy.manualScan.unavailable;
    if (runtime.isLoading || runtime.isFetching) runtimeMessage = runtimeCopy.manualScan.checking;
    else if (runtimeStatus?.manualScan.reason === 'oidc_managed') {
      runtimeMessage = runtimeCopy.manualScan.oidcManaged;
    } else if (runtimeStatus?.manualScan.reason === 'runner_unconfigured') {
      runtimeMessage = runtimeCopy.manualScan.runnerUnconfigured;
    } else if (runtimeStatus?.manualScan.reason === 'runner_unreachable') {
      runtimeMessage = runtimeCopy.manualScan.runnerUnavailable;
    }
    return (
      <EmptyState
        icon={<ShieldX className="size-6" />}
        title={sf.notRunTitle}
        description={sf.notRunDescription}
        primaryAction={
          canManage ? (
            <div className="flex max-w-md flex-col items-center gap-2">
              <Button
                loading={rescan.isPending}
                disabled={!scanAvailable || runtime.isFetching}
                onClick={runScan}
              >
                <PlayIcon className="size-4" />
                {sf.runScanButton}
              </Button>
              {!scanAvailable && <p className="text-xs text-muted-foreground">{runtimeMessage}</p>}
            </div>
          ) : undefined
        }
      />
    );
  }
  if (scanResults.findings.length === 0) {
    return (
      <EmptyState
        icon={<ShieldCheck className="size-6" />}
        title={sf.noFindingsTitle}
        description={sf.noFindingsDescription(scanResults.scannerVersion, scanResults.filesScanned)}
      />
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {sf.findingsCountLabel(scanResults.findings.length)} · {scanResults.status}
          {scanResults.truncated && sf.truncatedSuffix}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <SegmentedControl
            value={severity}
            onChange={setSeverity}
            options={options}
            ariaLabel={sf.severityFilterAriaLabel}
          />
        </div>
        {filtered.length === 0 ? (
          <EmptyState
            icon={<FilterX className="size-6" />}
            title={sf.noFindingsForSeverityTitle}
            description={sf.noFindingsForSeverityDescription}
            primaryAction={
              <Button variant="outline" onClick={() => setSeverity('all')}>
                {sf.clearFilterButton}
              </Button>
            }
          />
        ) : (
          CATEGORY_ORDER.map((category) => {
            const group = filtered.filter((f) => categorizeFinding(f.ruleId) === category);
            return group.length === 0 ? null : (
              <FindingGroup key={category} category={category} findings={group} copy={sf} />
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
